import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: '#F9FAFB' }}>
      <header className="bg-white shadow-sm border-b" style={{ borderColor: '#E5E7EB' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition">
            <ArrowLeft size={20} />
            Back to Home
          </button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>Privacy Policy</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <p className="text-sm text-gray-600 mb-8">Last Updated: {new Date().toLocaleDateString()}</p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              This Privacy Policy describes how we collect, use, store, and protect your personal information when you use our School Admission Portal. We are committed to protecting your privacy and ensuring the security of your personal data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>2. Information We Collect</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Personal Information:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>Parent/Guardian: Name, email address, phone number, address</li>
              <li>Pupil: Name, date of birth, place of birth, class sought, mother tongue</li>
              <li>Father's Details: Name, occupation, income, qualification, contact information, Aadhar number</li>
              <li>Mother's Details: Name, qualification, contact information, Aadhar number</li>
              <li>Documents: Birth certificate, Aadhar copies, photographs</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Payment Information:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>Payment details are processed through Razorpay (secure payment gateway)</li>
              <li>We do not store complete credit/debit card information</li>
              <li>Only transaction IDs and payment status are stored</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Technical Information:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>IP address, browser type, device information</li>
              <li>Login timestamps and activity logs</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>3. How We Use Your Information</h2>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>Processing admission applications</li>
              <li>Scheduling and conducting interviews</li>
              <li>Communicating application status updates</li>
              <li>Sending notifications via email and SMS</li>
              <li>Maintaining audit logs for administrative purposes</li>
              <li>Verifying documents and information provided</li>
              <li>Complying with legal and regulatory requirements</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>4. Data Storage and Security</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Security Measures:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>All data is stored on secure servers with encryption</li>
              <li>Passwords are hashed using industry-standard algorithms</li>
              <li>Access to data is restricted to authorized personnel only</li>
              <li>Regular security audits and updates</li>
              <li>Secure HTTPS connection for all data transmission</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Data Retention:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>Application data is retained for the current admission cycle</li>
              <li>Successful applications are archived as per school policy</li>
              <li>Unsuccessful applications may be deleted after the admission cycle</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>5. Information Sharing</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not sell, trade, or rent your personal information to third parties. Information may be shared with:
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li><strong>Payment Gateway:</strong> Razorpay processes payment transactions</li>
              <li><strong>Communication Services:</strong> Email (Gmail/Resend) and SMS services for notifications</li>
              <li><strong>School Administration:</strong> Authorized staff for admission processing</li>
              <li><strong>Legal Authorities:</strong> When required by law or legal process</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>6. Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">You have the right to:</p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your data (subject to legal requirements)</li>
              <li><strong>Withdrawal:</strong> Withdraw your application at any time</li>
              <li><strong>Objection:</strong> Object to processing of your data for specific purposes</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              To exercise these rights, please contact us through our Contact Us page.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>7. Cookies and Tracking</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our Portal uses minimal cookies for:
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>Maintaining login sessions</li>
              <li>Remembering user preferences</li>
              <li>Improving website performance</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              You can disable cookies in your browser settings, but this may affect functionality.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>8. Third-Party Services</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Razorpay:</strong> Payment processing is governed by Razorpay's privacy policy. Visit: https://razorpay.com/privacy/
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Email Services:</strong> Email communications are processed through Gmail SMTP, subject to Google's privacy policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>9. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              This Portal is designed for use by parents/guardians on behalf of minors. We collect information about children only with parental consent and for admission purposes. Parents have the right to review and request deletion of their child's information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>10. Data Breach Notification</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              In the unlikely event of a data breach, we will notify affected users within 72 hours via email and take immediate steps to secure the data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>11. Changes to Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may update this Privacy Policy from time to time. Users will be notified of significant changes via email. The "Last Updated" date at the top indicates the most recent revision.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>12. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For questions about this Privacy Policy or to exercise your rights, please contact us through our Contact Us page or email us at the provided contact information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>13. Compliance</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              This Privacy Policy complies with applicable data protection laws in India, including the Information Technology Act, 2000 and the Personal Data Protection Bill.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;